import { renderLatex } from "@/lib/katex-config";
import { expandLatexMetaTokens } from "@/lib/expand-latex-meta-tokens";

/**
 * Convert inline LML/Markdown/LaTeX-ish formatting markers to HTML for live preview.
 * Mirrors ParagraphBlock's static conversion and adds KaTeX for math.
 *
 * Supports:
 * - Inline math $...$
 * - Display math \[...\] and $$...$$
 * - LaTeX text commands: \emph, \textbf, \textit, \text, \cite, \ref, …
 * - Soft LaTeX refs like Theorem~(thm:pythagoras)
 * - LML markers @cite / @ref
 * - Markdown *bold* / _italic_ / etc.
 * - Bare meta tokens: \today, \and
 */
export function renderMarkersToHtml(text: string): string {
  if (!text) return "";

  // Normalise line endings once, up front. CRLF arrives from Windows pastes
  // and imported .tex files. Handling only \n further down left the orphaned
  // \r behind, and sanitizeToHtml then turned it into a SECOND <br> — so
  // pasted Windows text double-spaced in the preview. Doing it here rather
  // than at the <br> step also keeps \r out of the inline-math pattern
  // below, which excludes \n but not \r.
  const source = text.replace(/\r\n?/g, "\n");

  // Protect math spans BEFORE any markdown/LML processing. Marks like ^…^
  // would otherwise eat the ^ inside $c^2$ and produce mangled <sup> soup.
  const mathSpans: { latex: string; display: boolean }[] = [];
  const protectMath = (latex: string, display: boolean) => {
    const i = mathSpans.push({ latex: latex.trim(), display }) - 1;
    return "\uE000" + i + "\uE001";
  };

  let working = expandLatexMetaTokens(source);

  // Display math first (so $$ isn't partially eaten as two empty $).
  working = working.replace(/\\\[([\s\S]*?)\\\]/g, (_m, latex: string) =>
    protectMath(latex, true),
  );
  working = working.replace(/\$\$([\s\S]*?)\$\$/g, (_m, latex: string) =>
    protectMath(latex, true),
  );
  // Inline math
  working = working.replace(/\$([^$\n]+)\$/g, (_m, latex: string) =>
    protectMath(latex, false),
  );

  // Escape HTML entities
  let html = working
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Restore safe HTML tags that may appear in content
  html = html.replace(/&lt;br\s*\/?&gt;/gi, "<br>");

  // Preserve newlines as <br> inside multi-line theorem bodies so display
  // math (already protected) sits on its own line.
  html = html.replace(/\n/g, "<br>");

  // ── LaTeX text / font commands ──────────────────────────────────────────
  html = html.replace(
    /\\emph\{([^}]+)\}/g,
    "<em>$1</em>",
  );
  html = html.replace(
    /\\(?:textbf|mathbf)\{([^}]+)\}/g,
    "<strong>$1</strong>",
  );
  html = html.replace(
    /\\(?:textit|mathit)\{([^}]+)\}/g,
    "<em>$1</em>",
  );
  html = html.replace(
    /\\text\{([^}]+)\}/g,
    "$1",
  );
  // QED / end-of-proof markers inside body text — drop; proof block has its own □
  html = html.replace(/\\(?:blacksquare|qed|square)\b/g, "");

  // LaTeX-style cite / ref / url
  html = html.replace(
    /\\cite\{([^}]+)\}/g,
    '<span style="color: var(--lilia-primary, #3b82f6); font-size: 0.85em; font-weight: 500;">[$1]</span>',
  );
  html = html.replace(
    /\\(?:eqref|cref|Cref|autoref|pageref|nameref|ref)\{([^}]+)\}/g,
    '<span style="color: var(--lilia-primary, #3b82f6); font-size: 0.85em; text-decoration: underline; text-decoration-style: dotted;">$1</span>',
  );
  // Soft academic refs written as Theorem~(thm:pythagoras) or Theorem (thm:pythagoras)
  html = html.replace(
    /\b(Theorem|Lemma|Corollary|Proposition|Definition|Remark|Example|Figure|Table|Equation|Eq\.?)\s*~?\s*\(?((?:thm|lem|cor|prop|def|rem|ex|fig|tab|eq):[\w.:-]+)\)?/gi,
    (_m, kind: string, key: string) =>
      `${kind}&nbsp;<span style="color: var(--lilia-primary, #3b82f6); font-size: 0.9em; text-decoration: underline; text-decoration-style: dotted;">(${key})</span>`,
  );
  html = html.replace(
    /\\url\{([^}]+)\}/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: var(--lilia-primary, #3b82f6); text-decoration: underline;">$1</a>',
  );
  html = html.replace(
    /\\href\{([^}]+)\}\{([^}]+)\}/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: var(--lilia-primary, #3b82f6); text-decoration: underline;">$2</a>',
  );
  html = html.replace(
    /\\footnote\{([^}]+)\}/g,
    '<sup style="color: var(--lilia-primary, #3b82f6); cursor: help;" title="$1">*</sup>',
  );
  // \textsf{…} / \textrm{…} — FormatRailPanel FAMILY tiles
  html = html.replace(
    /\\textsf\{([^}]+)\}/g,
    '<span style="font-family: ui-sans-serif, Inter, system-ui, sans-serif;">$1</span>',
  );
  html = html.replace(
    /\\textrm\{([^}]+)\}/g,
    '<span style="font-family: Charter, Georgia, serif;">$1</span>',
  );
  html = html.replace(
    /\\texttt\{([^}]+)\}/g,
    '<code style="font-family: ui-monospace, SFMono-Regular, monospace; background: var(--lilia-surface, #f1f5f9); padding: 1px 4px; border-radius: 3px; font-size: 0.875em;">$1</code>',
  );
  html = html.replace(
    /\\textcolor\{([^}]+)\}\{([^}]+)\}/g,
    (_match, color: string, content: string) => {
      const colorMap: Record<string, string> = {
        red: "#ef4444", orange: "#f97316", amber: "#f59e0b", yellow: "#eab308",
        lime: "#84cc16", green: "#22c55e", emerald: "#10b981", teal: "#14b8a6",
        cyan: "#06b6d4", sky: "#0ea5e9", blue: "#3b82f6", indigo: "#6366f1",
        violet: "#8b5cf6", purple: "#a855f7", fuchsia: "#d946ef", pink: "#ec4899",
        rose: "#f43f5e", black: "#000000", gray: "#6b7280", darkgray: "#374151",
        lightgray: "#9ca3af", brown: "#92400e", olive: "#65a30d", navy: "#1e3a8a",
      };
      const resolvedColor = colorMap[color] || color;
      return `<span style="color: ${resolvedColor};">${content}</span>`;
    },
  );
  // LaTeX size group form: `{\large TEXT}`
  const SIZE_EM: Record<string, number> = {
    tiny: 0.5, scriptsize: 0.7, footnotesize: 0.8, small: 0.9,
    normalsize: 1.0, large: 1.2, Large: 1.44, LARGE: 1.728,
    huge: 2.074, Huge: 2.488,
  };
  html = html.replace(
    /\{\\(tiny|scriptsize|footnotesize|small|normalsize|large|Large|LARGE|huge|Huge)\s+([^{}]+)\}/g,
    (_match, size: string, content: string) => {
      const em = SIZE_EM[size] || 1.0;
      return `<span style="font-size: ${em}em;">${content}</span>`;
    },
  );
  html = html.replace(
    /\\hl(?:\[([^\]]+)\])?\{([^}]+)\}/g,
    (_match, color: string | undefined, content: string) => {
      const colorMap: Record<string, string> = {
        yellow: "#fef08a", orange: "#fed7aa", lime: "#d9f99d", green: "#bbf7d0",
        cyan: "#a5f3fc", sky: "#bae6fd", blue: "#bfdbfe", violet: "#ddd6fe",
        pink: "#fbcfe8", rose: "#fecdd3", gray: "#e5e7eb",
      };
      const bg = colorMap[color || "yellow"] || "#fef08a";
      return `<mark style="background-color: ${bg}; padding: 0 2px; border-radius: 2px;">${content}</mark>`;
    },
  );

  // Lilia-style markers
  html = html.replace(
    /@cite\{([^}]+)\}/g,
    '<span style="color: var(--lilia-primary, #3b82f6); font-size: 0.85em; font-weight: 500;">[$1]</span>',
  );
  html = html.replace(
    /@cite\[([^\]]+)\]/g,
    '<span style="color: var(--lilia-primary, #3b82f6); font-size: 0.85em; font-weight: 500;">[$1]</span>',
  );
  html = html.replace(
    /@ref\{([^}]+)\}/g,
    '<span style="color: var(--lilia-primary, #3b82f6); font-size: 0.85em; text-decoration: underline; text-decoration-style: dotted;">$1</span>',
  );
  html = html.replace(
    /@ref\[([^\]]+)\]/g,
    '<span style="color: var(--lilia-primary, #3b82f6); font-size: 0.85em; text-decoration: underline; text-decoration-style: dotted;">$1</span>',
  );

  // Markdown-style formatting (order: bold before italic)
  html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>");
  html = html.replace(/__([^_]+)__/g, "<u>$1</u>");
  html = html.replace(/(?<!\w)_([^_\n]+)_(?!\w)/g, "<em>$1</em>");
  html = html.replace(/~~([^~]+)~~/g, "<del>$1</del>");
  html = html.replace(
    /`([^`]+)`/g,
    '<code style="background: var(--lilia-surface, #f1f5f9); padding: 1px 4px; border-radius: 3px; font-size: 0.875em;">$1</code>',
  );

  // LML inline marks
  html = html.replace(/\^\^([^^]+)\^\^/g, '<span style="font-variant: small-caps;">$1</span>');
  html = html.replace(/(?<!\^)\^([^^\s][^^]*?)\^(?!\^)/g, "<sup>$1</sup>");
  html = html.replace(/%%([^%]+)%%/g, "<sub>$1</sub>");
  html = html.replace(/==([^=]+)==/g, '<mark style="background-color: #fef08a; padding: 0 2px; border-radius: 2px;">$1</mark>');
  html = html.replace(
    /\[%([\s\S]+?)%\]/g,
    '<span style="color: #9ca3af; text-decoration: line-through; font-style: italic; opacity: 0.75;">$1</span>',
  );

  // Restore protected math spans via KaTeX
  html = html.replace(/\uE000(\d+)\uE001/g, (_m, n: string) => {
    const span = mathSpans[Number(n)];
    if (!span) return "";
    const rendered = renderLatex(span.latex, { displayMode: span.display });
    if (span.display) {
      return `<div class="lilia-display-math" style="margin: 0.75em 0; text-align: center; overflow-x: auto;">${rendered}</div>`;
    }
    return rendered;
  });

  // Clean leftover empty QED glyphs glued after stripping \blacksquare
  html = html.replace(/\s*(?:■|□)\s*(?=<|$)/g, "");

  return html;
}
